/**
 * CloudWatch Integrations Config Section
 *
 * Expandable card for managing Bedrock and SageMaker connections.
 * Used in the full-page setup flow (IntegrationSetupPage).
 */

import { useState } from "react";
import { Plus, CheckCircle2, AlertCircle, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CloudWatchConnectionsList } from "./CloudWatchConnectionsList";
import { BedrockSetupInstructions } from "./BedrockSetupInstructions";
import { SageMakerSetupInstructions } from "./SageMakerSetupInstructions";
import { AWSBedrockLogo } from "./logos";
import { useBedrockSetupInstructions, useCreateBedrockConnection } from "@/hooks/useBedrock";
import { useSageMakerSetupInstructions, useCreateSageMakerConnection } from "@/hooks/useSagemaker";
import { AWS_BEDROCK_REGIONS } from "@/constants/aws";
import type { BedrockConnectionCreate } from "@/types/bedrock";
import type { SageMakerConnectionCreate } from "@/types/sagemaker";

interface CloudWatchConfigSectionProps {
  expanded?: boolean;
  onExpandChange?: (expanded: boolean) => void;
  onConnectionCreated?: () => void;
}

const BEDROCK_DEFAULTS: Omit<BedrockConnectionCreate, "external_id"> = {
  connection_name: "",
  role_arn: "",
  aws_region: "us-east-1",
  log_group_name: "/aws/bedrock/modelinvocations",
};

const SAGEMAKER_DEFAULTS: Omit<SageMakerConnectionCreate, "external_id"> = {
  connection_name: "",
  role_arn: "",
  aws_region: "us-east-1",
  log_group_name: "/aws/sagemaker/Endpoints",
};

export function CloudWatchConfigSection({
  expanded = false,
  onExpandChange,
  onConnectionCreated,
}: CloudWatchConfigSectionProps) {
  const [activeTab, setActiveTab] = useState<"bedrock" | "sagemaker">("bedrock");
  const [showAddForm, setShowAddForm] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);
  const [bedrockForm, setBedrockForm] =
    useState<Omit<BedrockConnectionCreate, "external_id">>(BEDROCK_DEFAULTS);
  const [sagemakerForm, setSagemakerForm] =
    useState<Omit<SageMakerConnectionCreate, "external_id">>(SAGEMAKER_DEFAULTS);

  const { data: bedrockInstructions, isLoading: bedrockInstructionsLoading } =
    useBedrockSetupInstructions(showAddForm && activeTab === "bedrock");
  const { data: sagemakerInstructions, isLoading: sagemakerInstructionsLoading } =
    useSageMakerSetupInstructions(showAddForm && activeTab === "sagemaker");

  const bedrockCreate = useCreateBedrockConnection();
  const sagemakerCreate = useCreateSageMakerConnection();

  const handleReset = () => {
    setShowAddForm(false);
    setShowInstructions(true);
    setBedrockForm(BEDROCK_DEFAULTS);
    setSagemakerForm(SAGEMAKER_DEFAULTS);
    bedrockCreate.reset();
    sagemakerCreate.reset();
  };

  const handleBedrockSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bedrockInstructions) return;
    try {
      await bedrockCreate.mutateAsync({
        ...bedrockForm,
        connection_name: bedrockForm.connection_name.trim(),
        role_arn: bedrockForm.role_arn.trim(),
        log_group_name: bedrockForm.log_group_name.trim(),
        external_id: bedrockInstructions.external_id,
      });
      onConnectionCreated?.();
      setTimeout(() => handleReset(), 1500);
    } catch {
      // error rendered via bedrockCreate.error
    }
  };

  const handleSageMakerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sagemakerInstructions) return;
    try {
      await sagemakerCreate.mutateAsync({
        ...sagemakerForm,
        connection_name: sagemakerForm.connection_name.trim(),
        role_arn: sagemakerForm.role_arn.trim(),
        log_group_name: sagemakerForm.log_group_name.trim(),
        external_id: sagemakerInstructions.external_id,
      });
      onConnectionCreated?.();
      setTimeout(() => handleReset(), 1500);
    } catch {
      // error rendered via sagemakerCreate.error
    }
  };

  const bedrockFormValid =
    bedrockForm.connection_name.trim() &&
    bedrockForm.role_arn.trim().startsWith("arn:aws:iam::") &&
    bedrockForm.log_group_name.trim() &&
    !!bedrockInstructions;

  const sagemakerFormValid =
    sagemakerForm.connection_name.trim() &&
    sagemakerForm.role_arn.trim().startsWith("arn:aws:iam::") &&
    sagemakerForm.log_group_name.trim() &&
    !!sagemakerInstructions;

  return (
    <Card className={expanded ? "ring-2 ring-primary" : ""}>
      <CardHeader
        className="cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => onExpandChange?.(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-muted/80 to-muted/40 flex items-center justify-center shadow-sm">
              <AWSBedrockLogo className="w-8 h-8" />
            </div>
            <div>
              <CardTitle className="text-lg">AWS CloudWatch Integrations</CardTitle>
              <CardDescription className="text-sm">
                Ingest Bedrock and SageMaker model invocation logs for AI security monitoring
              </CardDescription>
            </div>
          </div>
          <Button variant="ghost" size="icon">
            {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </Button>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="space-y-6 pt-0">
          <Separator />

          <div>
            <h3 className="text-sm font-medium mb-3">Configured Connections</h3>
            <CloudWatchConnectionsList />
          </div>

          <Separator />

          <div>
            {!showAddForm ? (
              <Button variant="outline" className="w-full" onClick={() => setShowAddForm(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Connection
              </Button>
            ) : (
              <div className="space-y-5">
                <h3 className="text-sm font-medium">Add New Connection</h3>

                <Tabs
                  value={activeTab}
                  onValueChange={(v) => {
                    setActiveTab(v as "bedrock" | "sagemaker");
                    setShowInstructions(true);
                    bedrockCreate.reset();
                    sagemakerCreate.reset();
                  }}
                >
                  <TabsList className="w-full">
                    <TabsTrigger value="bedrock" className="flex-1">
                      AWS Bedrock
                    </TabsTrigger>
                    <TabsTrigger value="sagemaker" className="flex-1">
                      AWS SageMaker
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="bedrock">
                    <form onSubmit={handleBedrockSubmit} className="space-y-5 pt-4">
                      <InstructionsAccordion
                        open={showInstructions}
                        onToggle={() => setShowInstructions((v) => !v)}
                      >
                        <BedrockSetupInstructions
                          setupInstructions={bedrockInstructions}
                          isLoading={bedrockInstructionsLoading}
                        />
                      </InstructionsAccordion>

                      <SectionFormFields
                        idPrefix="cs-bedrock"
                        formData={bedrockForm}
                        onChange={setBedrockForm}
                        placeholders={{
                          name: "prod-bedrock",
                          arn: "arn:aws:iam::123456789012:role/VortalBedrockRole",
                          logGroup: "/aws/bedrock/modelinvocations",
                        }}
                        isSuccess={bedrockCreate.isSuccess}
                        isError={bedrockCreate.isError}
                        errorMessage={bedrockCreate.error?.message}
                        isPending={bedrockCreate.isPending}
                        isFormValid={!!bedrockFormValid}
                        onCancel={handleReset}
                      />
                    </form>
                  </TabsContent>

                  <TabsContent value="sagemaker">
                    <form onSubmit={handleSageMakerSubmit} className="space-y-5 pt-4">
                      <InstructionsAccordion
                        open={showInstructions}
                        onToggle={() => setShowInstructions((v) => !v)}
                      >
                        <SageMakerSetupInstructions
                          setupInstructions={sagemakerInstructions}
                          isLoading={sagemakerInstructionsLoading}
                        />
                      </InstructionsAccordion>

                      <SectionFormFields
                        idPrefix="cs-sagemaker"
                        formData={sagemakerForm}
                        onChange={setSagemakerForm}
                        placeholders={{
                          name: "prod-sagemaker",
                          arn: "arn:aws:iam::123456789012:role/VortalSageMakerRole",
                          logGroup: "/aws/sagemaker/Endpoints",
                        }}
                        isSuccess={sagemakerCreate.isSuccess}
                        isError={sagemakerCreate.isError}
                        errorMessage={sagemakerCreate.error?.message}
                        isPending={sagemakerCreate.isPending}
                        isFormValid={!!sagemakerFormValid}
                        onCancel={handleReset}
                      />
                    </form>
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function InstructionsAccordion({
  open,
  onToggle,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-muted/30">
      <button
        type="button"
        className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium"
        onClick={onToggle}
      >
        <span>Step 1 — Configure AWS IAM Role</span>
        {open ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
      {open && <div className="px-4 pb-4 border-t">{children}</div>}
    </div>
  );
}

interface FormData {
  connection_name: string;
  role_arn: string;
  aws_region: string;
  log_group_name: string;
}

interface SectionFormFieldsProps {
  idPrefix: string;
  formData: FormData;
  onChange: (data: FormData) => void;
  placeholders: { name: string; arn: string; logGroup: string };
  isSuccess: boolean;
  isError: boolean;
  errorMessage?: string;
  isPending: boolean;
  isFormValid: boolean;
  onCancel: () => void;
}

function SectionFormFields({
  idPrefix,
  formData,
  onChange,
  placeholders,
  isSuccess,
  isError,
  errorMessage,
  isPending,
  isFormValid,
  onCancel,
}: SectionFormFieldsProps) {
  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-muted-foreground">Step 2 — Enter Connection Details</h4>

      {isSuccess && (
        <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200">
          <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Connection validated successfully!</p>
            <p className="text-xs mt-1">The first data pull has been enqueued.</p>
          </div>
        </div>
      )}

      {isError && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Connection validation failed</p>
            <p className="text-xs mt-1">
              {errorMessage ||
                "Unable to assume the IAM role. Verify the role ARN, external ID, and trust policy."}
            </p>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-name`}>
          Connection Name <span className="text-destructive">*</span>
        </Label>
        <Input
          id={`${idPrefix}-name`}
          placeholder={placeholders.name}
          value={formData.connection_name}
          onChange={(e) => onChange({ ...formData, connection_name: e.target.value })}
          autoComplete="off"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-arn`}>
          IAM Role ARN <span className="text-destructive">*</span>
        </Label>
        <Input
          id={`${idPrefix}-arn`}
          placeholder={placeholders.arn}
          value={formData.role_arn}
          onChange={(e) => onChange({ ...formData, role_arn: e.target.value })}
          autoComplete="off"
          required
        />
        <p className="text-xs text-muted-foreground">
          The ARN of the cross-account IAM role you created in Step 1
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-region`}>AWS Region</Label>
          <Select
            value={formData.aws_region}
            onValueChange={(value) => onChange({ ...formData, aws_region: value })}
          >
            <SelectTrigger id={`${idPrefix}-region`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AWS_BEDROCK_REGIONS.map((region) => (
                <SelectItem key={region} value={region}>
                  {region}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-log-group`}>
            Log Group Name <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`${idPrefix}-log-group`}
            placeholder={placeholders.logGroup}
            value={formData.log_group_name}
            onChange={(e) => onChange({ ...formData, log_group_name: e.target.value })}
            autoComplete="off"
            required
          />
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={!isFormValid || isPending}>
          {isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Validating…
            </>
          ) : (
            "Add Connection"
          )}
        </Button>
      </div>
    </div>
  );
}
